import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Bell,
  MessageSquare,
  CheckCheck,
  RefreshCw,
  X,
  Mail,
  Clock,
  User,
  Building2,
  ExternalLink,
  ChevronRight,
  Frown,
  Meh,
  Smile,
  Laugh,
} from "lucide-react";
import { toast } from "sonner";
import {
  getFeedbackSummary,
  updateFeedbackStatus,
  markAllFeedbackRead,
} from "../api/feedbackApi";
import type {
  FeedbackItem,
  FeedbackRating,
  FeedbackStatus,
} from "../api/feedbackApi";
import FeedbackDetailModal from "./FeedbackDetailModal";
import "../styles/feedback-notification.css";

function formatTimeAgo(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffSec < 60) return "Just now";
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `${diffHour}h ago`;
    const diffDays = Math.floor(diffHour / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  } catch {
    return "";
  }
}

function getRatingIcon(rating: FeedbackRating) {
  switch (rating) {
    case "bad":
      return <Frown size={14} />;
    case "okay":
      return <Meh size={14} />;
    case "good":
      return <Smile size={14} />;
    case "amazing":
      return <Laugh size={14} />;
    default:
      return <Smile size={14} />;
  }
}

export default function FeedbackNotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [recentFeedbacks, setRecentFeedbacks] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeFeedback, setActiveFeedback] = useState<FeedbackItem | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getFeedbackSummary();
      setUnreadCount(data.unread_count);
      setTotalCount(data.total_count);
      setRecentFeedbacks(data.recent);
    } catch (err) {
      // Admin check failed or network issue
      console.error("Error fetching feedback summary:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll on mount and periodically
  useEffect(() => {
    fetchSummary();
    const interval = setInterval(fetchSummary, 25000);
    const handleFocus = () => fetchSummary();
    window.addEventListener("focus", handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
    };
  }, [fetchSummary]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        !activeFeedback
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, activeFeedback]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (activeFeedback) {
          setActiveFeedback(null);
        } else if (isOpen) {
          setIsOpen(false);
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, activeFeedback]);

  const handleMarkAllRead = async () => {
    try {
      const res = await markAllFeedbackRead();
      toast.success(`Marked ${res.modified_count} feedback(s) as reviewed`);
      setUnreadCount(0);
      setRecentFeedbacks((prev) =>
        prev.map((item) =>
          item.status === "unread" ? { ...item, status: "reviewed" } : item
        )
      );
    } catch {
      toast.error("Failed to mark feedbacks as read");
    }
  };

  const handleStatusChange = async (feedbackId: string, newStatus: FeedbackStatus) => {
    try {
      const res = await updateFeedbackStatus(feedbackId, newStatus);
      const updatedItem = res.data;

      // Update recent feedbacks
      setRecentFeedbacks((prev) =>
        prev.map((item) => (item.id === feedbackId ? updatedItem : item))
      );

      if (activeFeedback?.id === feedbackId) {
        setActiveFeedback(updatedItem);
      }

      // Recalculate unread count
      setRecentFeedbacks((current) => {
        const count = current.filter((x) => x.status === "unread").length;
        setUnreadCount(count);
        return current;
      });

      toast.success(`Feedback status updated to ${newStatus}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const openFeedbackDetail = (item: FeedbackItem) => {
    setActiveFeedback(item);
    // If it was unread, automatically mark as reviewed
    if (item.status === "unread") {
      handleStatusChange(item.id, "reviewed");
    }
  };

  return (
    <div className="feedback-bell-wrapper" ref={containerRef}>
      {/* Bell Button */}
      <button
        type="button"
        className={`feedback-bell-btn ${isOpen ? "is-active" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="Admin Feedback Notifications"
        aria-expanded={isOpen}
        title={
          unreadCount > 0
            ? `${unreadCount} unread feedback${unreadCount > 1 ? "s" : ""}`
            : "Feedback Notifications"
        }
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <>
            <span className="feedback-badge-pulse" />
            <span className="feedback-badge">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          </>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="feedback-dropdown-panel" role="region" aria-label="Feedback Notifications">
          {/* Header */}
          <div className="feedback-dropdown-header">
            <div className="feedback-header-title-wrap">
              <span className="feedback-header-title">Feedback Notifications</span>
              {unreadCount > 0 && (
                <span className="feedback-unread-chip">{unreadCount} New</span>
              )}
            </div>
            <div className="feedback-header-actions">
              {unreadCount > 0 && (
                <button
                  type="button"
                  className="feedback-action-text-btn"
                  onClick={handleMarkAllRead}
                  title="Mark all as reviewed"
                >
                  <CheckCheck size={14} />
                  <span>Mark read</span>
                </button>
              )}
              <button
                type="button"
                className="feedback-action-text-btn"
                onClick={fetchSummary}
                title="Refresh"
              >
                <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
              </button>
            </div>
          </div>

          {/* Feedback Items List */}
          <div className="feedback-items-list">
            {recentFeedbacks.length === 0 ? (
              <div className="feedback-empty-state">
                <MessageSquare size={28} />
                <span>No feedback submitted yet</span>
              </div>
            ) : (
              recentFeedbacks.map((item) => (
                <div
                  key={item.id}
                  className={`feedback-item-card ${
                    item.status === "unread" ? "is-unread" : ""
                  }`}
                  onClick={() => openFeedbackDetail(item)}
                >
                  <div className="feedback-item-top">
                    <div className="feedback-user-info">
                      <span className="feedback-user-name">
                        {item.user_name || item.user_email || "Anonymous User"}
                      </span>
                      {item.user_role && (
                        <span className="feedback-user-role-badge">
                          {item.user_role}
                        </span>
                      )}
                    </div>
                    <span className={`feedback-rating-pill ${item.rating}`}>
                      {getRatingIcon(item.rating)}
                      {item.rating}
                    </span>
                  </div>

                  <p className="feedback-item-text">
                    {item.suggestions || "(No written suggestions provided)"}
                  </p>

                  <div className="feedback-item-footer">
                    <span>{item.user_organization || item.user_email}</span>
                    <span>{formatTimeAgo(item.created_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="feedback-dropdown-footer">
            <span style={{ fontSize: "11px", color: "#64748b" }}>
              Total Feedbacks: {totalCount}
            </span>
            <button
              type="button"
              className="feedback-view-all-btn"
              style={{ width: "auto" }}
              onClick={() => {
                setIsOpen(false);
                window.location.href = "/feedback";
              }}
            >
              Feedback Page <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Full Detail Modal when clicked */}
      <FeedbackDetailModal
        feedback={activeFeedback}
        onClose={() => setActiveFeedback(null)}
        onStatusChange={handleStatusChange}
      />
    </div>
  );
}
